import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiBookOpen,
  FiBriefcase,
  FiArrowRight,
  FiClock,
  FiUser,
  FiCalendar,
  FiBookmark,
} from "react-icons/fi";
import AxiosInstance from "../Home/AxiosInstance";
import TraineeHeader from "./TraineeHeader";
import { fetchSavedItems } from "./SaveButton";
import Footers from "../Home/Footers";
import "../Home/theme.css";
import "./TraineeProfile.css";
import {
  getCourseLogo,
  getInstructorName,
  openCourse,
  sortByDateDesc,
  statusClass,
} from "./TraineeListPages";

const DASH_ENROLLMENTS = 3;
const DASH_APPLICATIONS = 5;

export default function TraineeProfile() {
  const [enrollments, setEnrollments] = useState([]);
  const [applications, setApplications] = useState([]);
  const [jobApplications, setJobApplications] = useState([]);
  const [matches, setMatches] = useState({
    has_preference: false,
    jobs: [],
    internships: [],
    trainings: [],
    message: "",
  });
  const [traineeName, setTraineeName] = useState("");
  const [savedCount, setSavedCount] = useState(0);
  const navigate = useNavigate();
  const catalogState = { from: "trainee-profile" };

  useEffect(() => {
    const traineeId = localStorage.getItem("traineeId");
    if (!traineeId) {
      alert("Trainee ID is missing. Please log in.");
      return;
    }

    AxiosInstance.get(`companies/trainee/enrollments/${traineeId}/`)
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : [];
        setEnrollments(
          sortByDateDesc(data, (item) => item.enrolled_at || item.created_at)
        );
      })
      .catch((error) => console.error("Error fetching enrollment:", error));

    AxiosInstance.get(`companies/trainee-details/${traineeId}`)
      .then((res) => {
        const name = `${res.data?.first_name || ""} ${
          res.data?.last_name || ""
        }`.trim();
        setTraineeName(name);
      })
      .catch(() => {});

    fetchSavedItems(traineeId)
      .then(({ items }) => setSavedCount(items.length))
      .catch(() => setSavedCount(0));

    AxiosInstance.get(`companies/applications/${traineeId}/`)
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : [];
        setApplications(
          sortByDateDesc(
            data,
            (item) => item.application_date || item.created_at
          )
        );
      })
      .catch((err) => {
        console.error("Error fetching applications:", err);
        setApplications([]);
      });

    AxiosInstance.get(`companies/job-applications/${traineeId}/`)
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : [];
        setJobApplications(
          sortByDateDesc(
            data,
            (item) => item.application_date || item.created_at
          )
        );
      })
      .catch((err) => {
        console.error("Error fetching job applications:", err);
        setJobApplications([]);
      });

    AxiosInstance.get(`companies/career-preference/${traineeId}/matches/`)
      .then((res) => {
        setMatches({
          has_preference: Boolean(res.data?.has_preference),
          jobs: Array.isArray(res.data?.jobs) ? res.data.jobs : [],
          internships: Array.isArray(res.data?.internships)
            ? res.data.internships
            : [],
          trainings: Array.isArray(res.data?.trainings) ? res.data.trainings : [],
          message: res.data?.message || "",
        });
      })
      .catch((err) => {
        console.error("Error fetching career matches:", err);
        setMatches({
          has_preference: false,
          jobs: [],
          internships: [],
          trainings: [],
          message: "",
        });
      });
  }, []);

  const latestEnrollments = useMemo(
    () => enrollments.slice(0, DASH_ENROLLMENTS),
    [enrollments]
  );
  const latestInternshipApps = useMemo(
    () => applications.slice(0, DASH_APPLICATIONS),
    [applications]
  );
  const latestJobApps = useMemo(
    () => jobApplications.slice(0, DASH_APPLICATIONS),
    [jobApplications]
  );

  const firstName = traineeName.split(" ")[0] || "there";

  return (
    <div className="TraineePage als-trainee-dash">
      <TraineeHeader />

      <section className="tp-hero">
        <div className="tp-hero-inner tp-hero-inner--simple">
          <div className="tp-hero-copy">
            <p className="tp-eyebrow">Dashboard</p>
            <h1>
              Welcome back{firstName !== "there" ? `, ${firstName}` : ""}
            </h1>
            <p>
              Pick up where you left off, or explore new courses, internships,
              and jobs.
            </p>
          </div>
          <div className="tp-hero-stats">
            <button
              type="button"
              className="tp-stat"
              onClick={() => navigate("/my-enrollments")}
            >
              <FiBookOpen />
              <div>
                <strong>{enrollments.length}</strong>
                <span>Enrollments</span>
              </div>
            </button>
            <button
              type="button"
              className="tp-stat"
              onClick={() =>
                navigate(
                  applications.length > 0
                    ? "/my-internship-applications"
                    : "/my-job-applications"
                )
              }
            >
              <FiBriefcase />
              <div>
                <strong>
                  {applications.length + jobApplications.length}
                </strong>
                <span>Applications</span>
              </div>
            </button>
            <button
              type="button"
              className="tp-stat"
              onClick={() => navigate("/my-saved")}
            >
              <FiBookmark />
              <div>
                <strong>{savedCount}</strong>
                <span>Wishlist</span>
              </div>
            </button>
          </div>
        </div>
      </section>

      <main className="tp-body">
        <section className="tp-panel" id="matched-opportunities">
          <div className="tp-panel-head tp-panel-head--row">
            <div>
              <h2>Matched for you</h2>
              <p>
                Based on your career preference — jobs, internships, and
                trainings.
              </p>
            </div>
            <Link to="/matched-opportunities" className="tp-section-link">
              View all <FiArrowRight />
            </Link>
          </div>

          {!matches.has_preference ? (
            <div className="tp-empty">
              <FiUser />
              <h3>Set your career preference</h3>
              <p>
                {matches.message ||
                  "Add preferred roles, locations, and salary to see matched opportunities here."}
              </p>
              <Link to="/trainee-career-preference" className="tp-btn-primary">
                Add preference
              </Link>
            </div>
          ) : !matches.jobs.length &&
            !matches.internships.length &&
            !matches.trainings.length ? (
            <div className="tp-empty">
              <FiBriefcase />
              <h3>No strong matches yet</h3>
              <p>
                Try broadening preferred roles or locations, or browse the full
                catalogs.
              </p>
              <Link to="/matched-opportunities" className="tp-btn-primary">
                View all matches
              </Link>
            </div>
          ) : (
            <div className="tp-match-grid">
              {[
                ...matches.jobs.slice(0, 2).map((job) => (
                  <article key={`job-${job.job_id}`} className="tp-match-card">
                    <span className="tp-match-badge">
                      Job · Match {job.match_score}
                    </span>
                    <h3>{job.role || "Role"}</h3>
                    <p>
                      {job.company?.company_name || "Company"}
                      {job.location ? ` · ${job.location}` : ""}
                    </p>
                    {job.match_reasons?.length ? (
                      <small>{job.match_reasons.join(" · ")}</small>
                    ) : null}
                    <Link
                      to={`/jobs/${job.job_id}`}
                      state={catalogState}
                      className="tp-section-link"
                    >
                      View job <FiArrowRight />
                    </Link>
                  </article>
                )),
                ...matches.internships.slice(0, 2).map((item) => (
                  <article
                    key={`intern-${item.internship_id}`}
                    className="tp-match-card"
                  >
                    <span className="tp-match-badge">
                      Internship · Match {item.match_score}
                    </span>
                    <h3>{item.role || "Role"}</h3>
                    <p>
                      {item.company?.company_name || "Company"}
                      {item.location ? ` · ${item.location}` : ""}
                    </p>
                    {item.match_reasons?.length ? (
                      <small>{item.match_reasons.join(" · ")}</small>
                    ) : null}
                    <Link
                      to={`/internships/${item.internship_id}`}
                      state={catalogState}
                      className="tp-section-link"
                    >
                      View internship <FiArrowRight />
                    </Link>
                  </article>
                )),
                ...matches.trainings.slice(0, 2).map((batch) => {
                  const courseName =
                    batch.course_material?.course?.course_name ||
                    batch.batch_name ||
                    "Training";
                  return (
                    <article
                      key={`batch-${batch.batch_id}`}
                      className="tp-match-card"
                    >
                      <span className="tp-match-badge">
                        Training · Match {batch.match_score}
                      </span>
                      <h3>{courseName}</h3>
                      <p>
                        {batch.mode || "Mode"}
                        {batch.venue_address ? ` · ${batch.venue_address}` : ""}
                      </p>
                      {batch.match_reasons?.length ? (
                        <small>{batch.match_reasons.join(" · ")}</small>
                      ) : null}
                      <Link
                        to={`/courses/${batch.batch_id}`}
                        state={catalogState}
                        className="tp-section-link"
                      >
                        View training <FiArrowRight />
                      </Link>
                    </article>
                  );
                }),
              ]}
            </div>
          )}
        </section>

        <section className="tp-panel" id="trainee-enrollments">
          <div className="tp-panel-head tp-panel-head--row">
            <div>
              <h2>Continue learning</h2>
              <p>Your latest enrolled courses — click a card to resume.</p>
            </div>
            <Link to="/my-enrollments" className="tp-section-link">
              View all enrollments <FiArrowRight />
            </Link>
          </div>

          {latestEnrollments.length === 0 ? (
            <div className="tp-empty-state">
              <FiBookOpen />
              <h3>No enrollments yet</h3>
              <p>Browse courses and enroll to start learning.</p>
              <Link
                to="/courses"
                state={catalogState}
                className="tp-btn-primary"
              >
                Browse courses
              </Link>
            </div>
          ) : (
            <div className="tp-course-grid">
              {latestEnrollments.map((enrollment) => {
                const courseName =
                  enrollment.batch?.course_material?.course?.course_name ||
                  "Course";
                const batchName = enrollment.batch?.batch_name;
                const instructor = getInstructorName(enrollment);
                const logo = getCourseLogo(enrollment);
                const enrolledAt = enrollment.enrolled_at
                  ? new Date(enrollment.enrolled_at).toLocaleDateString()
                  : null;

                return (
                  <article
                    key={enrollment.enrollment_id}
                    className="tp-course-card"
                  >
                    <div
                      className="tp-course-media"
                      style={
                        !logo
                          ? {
                              background:
                                "linear-gradient(135deg, #5b5fff22, #0b0e2722)",
                            }
                          : undefined
                      }
                    >
                      {logo ? (
                        <img src={logo} alt="" />
                      ) : (
                        <span>{courseName.charAt(0)}</span>
                      )}
                    </div>
                    <div className="tp-course-body">
                      <div className="tp-course-top">
                        <span className={statusClass(enrollment.is_enrolled)}>
                          {String(enrollment.is_enrolled || "Enrolled")}
                        </span>
                      </div>
                      <h3>{courseName}</h3>
                      <p className="tp-course-meta">
                        <FiUser /> {instructor}
                        {batchName ? ` · ${batchName}` : ""}
                      </p>
                      {enrolledAt && (
                        <p className="tp-course-meta">
                          <FiCalendar /> Enrolled {enrolledAt}
                        </p>
                      )}
                      <button
                        type="button"
                        className="tp-btn-primary tp-course-cta"
                        onClick={() => openCourse(navigate, enrollment)}
                      >
                        Continue learning <FiArrowRight />
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        <section className="tp-panel" id="trainee-internship-applications">
          <div className="tp-panel-head tp-panel-head--row">
            <div>
              <h2>Internship applications</h2>
              <p>Your latest internship applications.</p>
            </div>
            <Link
              to="/my-internship-applications"
              className="tp-section-link"
            >
              View all internships <FiArrowRight />
            </Link>
          </div>

          {latestInternshipApps.length === 0 ? (
            <div className="tp-empty-state">
              <FiBriefcase />
              <h3>No applications yet</h3>
              <p>Find internships and apply to start tracking here.</p>
              <Link
                to="/internships"
                state={catalogState}
                className="tp-btn-primary"
              >
                Browse internships
              </Link>
            </div>
          ) : (
            <ul className="tp-app-list">
              {latestInternshipApps.map((app) => {
                const internshipId = app.internship?.internship_id;
                const role = app.internship?.role || "Internship";
                const applied = app.application_date
                  ? new Date(app.application_date).toLocaleDateString()
                  : null;
                const resumeUrl = app.resume_path
                  ? `${AxiosInstance.defaults.baseURL}media/${app.resume_path}`
                  : null;

                return (
                  <li key={app.application_id} className="tp-app-item">
                    <div className="tp-app-main">
                      <button
                        type="button"
                        className="tp-link-btn tp-app-role"
                        onClick={() =>
                          navigate(`/internships/${internshipId}`, {
                            state: catalogState,
                          })
                        }
                      >
                        {role}
                      </button>
                      <div className="tp-app-meta">
                        {applied && (
                          <span>
                            <FiClock /> Applied {applied}
                          </span>
                        )}
                        {resumeUrl && (
                          <a
                            href={resumeUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="tp-text-link"
                          >
                            View resume
                          </a>
                        )}
                      </div>
                      {app.feedback && (
                        <p className="tp-app-feedback">{app.feedback}</p>
                      )}
                    </div>
                    <div className="tp-app-status">
                      <span className={statusClass(app.status)}>
                        {app.status || "—"}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="tp-panel" id="trainee-job-applications">
          <div className="tp-panel-head tp-panel-head--row">
            <div>
              <h2>Job applications</h2>
              <p>Your latest job applications.</p>
            </div>
            <Link to="/my-job-applications" className="tp-section-link">
              View all jobs <FiArrowRight />
            </Link>
          </div>

          {latestJobApps.length === 0 ? (
            <div className="tp-empty-state">
              <FiBriefcase />
              <h3>No job applications yet</h3>
              <p>Find jobs and apply to start tracking here.</p>
              <Link to="/jobs" state={catalogState} className="tp-btn-primary">
                Browse jobs
              </Link>
            </div>
          ) : (
            <ul className="tp-app-list">
              {latestJobApps.map((app) => {
                const jobId = app.job?.job_id;
                const role = app.job?.role || "Job";
                const applied = app.application_date
                  ? new Date(app.application_date).toLocaleDateString()
                  : null;
                const resumeUrl = app.resume_path
                  ? `${AxiosInstance.defaults.baseURL}media/${app.resume_path}`
                  : null;

                return (
                  <li key={app.application_id} className="tp-app-item">
                    <div className="tp-app-main">
                      <button
                        type="button"
                        className="tp-link-btn tp-app-role"
                        onClick={() =>
                          navigate(`/jobs/${jobId}`, {
                            state: catalogState,
                          })
                        }
                      >
                        {role}
                      </button>
                      <div className="tp-app-meta">
                        {applied && (
                          <span>
                            <FiClock /> Applied {applied}
                          </span>
                        )}
                        {resumeUrl && (
                          <a
                            href={resumeUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="tp-text-link"
                          >
                            View resume
                          </a>
                        )}
                      </div>
                      {app.feedback && (
                        <p className="tp-app-feedback">{app.feedback}</p>
                      )}
                    </div>
                    <div className="tp-app-status">
                      <span className={statusClass(app.status)}>
                        {app.status || "—"}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </main>

      <Footers />
    </div>
  );
}
